import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@auth/AuthProvider';
import { VolunteerTeamBundle } from '@data/interfaces/Volunteering/volunteerteam.interface';
import VolunteeringController from '@data/controller/VolunteeringController';
import i18next from 'i18next';

// A single team with its members and roles, for the team's own page. Separate
// from useVolunteerTeams so that page doesn't load every team to show one.
export default function useVolunteerTeam(teamID?: string) {
  const auth = useAuth();
  const churchId = auth.churchId;

  const [bundle, setBundle] = useState<VolunteerTeamBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchId || !teamID) return;

    setLoading(true);
    setError(null);

    try {
      const response = await VolunteeringController.fetchTeam(churchId, teamID, auth);
      if (response.error) {
        setError(response.error);
        setBundle(null);
      } else {
        setBundle(response.body?.result ?? null);
      }
    } catch (err: any) {
      setError(err?.message || i18next.t('volunteering:errors.loadTeam'));
      setBundle(null);
    } finally {
      setLoading(false);
    }
  }, [churchId, teamID, auth]);

  useEffect(() => {
    if (!churchId || !teamID) return;

    void fetch();
  }, [churchId, teamID, fetch]);

  return { bundle, loading, error, refetch: fetch } as const;
}
